"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

/**
 * Podpowiedzi Synthesis: kafelek informacyjny po najechaniu zamiast natywnego
 * `title` przeglądarki. Element dostaje `data-podpowiedz="…"` — jeden nasłuch
 * na dokumencie obsługuje całą apkę (też komponenty `memo` i portale), bez
 * owijania elementów. Klawiatura: kafelek przy fokusie z klawiatury.
 * Przycisk pomocy „?" (`.pomoc[data-podpowiedz]`) działa też na klik i dotyk:
 * klik pokazuje kafelek od razu, drugi klik albo dotyk obok go chowa.
 */

const ATRYBUT = "data-podpowiedz";
const OPOZNIENIE_MS = 350;
const MARGINES = 8;
const POMOC = `.pomoc[${ATRYBUT}]`;

interface Stan {
  tekst: string;
  cel: DOMRect;
}

function elementZPodpowiedzia(x: EventTarget | null): Element | null {
  return x instanceof Element ? x.closest(`[${ATRYBUT}]`) : null;
}

export default function Podpowiedzi() {
  const [stan, setStan] = useState<Stan | null>(null);
  const [pozycja, setPozycja] = useState<{ left: number; top: number } | null>(null);
  const kafelek = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cel: Element | null = null;
    let pokazany: Element | null = null; // kafelek faktycznie widoczny (po opóźnieniu)
    let zegar: ReturnType<typeof setTimeout> | undefined;

    const ukryj = () => {
      cel = null;
      pokazany = null;
      clearTimeout(zegar);
      setStan(null);
    };
    const pokaz = (el: Element, odRazu = false) => {
      cel = el;
      clearTimeout(zegar);
      const odpal = () => {
        const tekst = el.getAttribute(ATRYBUT)?.trim();
        if (!el.isConnected || !tekst) return;
        pokazany = el;
        setPozycja(null);
        setStan({ tekst, cel: el.getBoundingClientRect() });
      };
      if (odRazu) odpal();
      else zegar = setTimeout(odpal, OPOZNIENIE_MS);
    };

    const naNajechanie = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      const el = elementZPodpowiedzia(e.target);
      if (el === cel) return;
      if (el) pokaz(el);
      else ukryj();
    };
    const naFokus = (e: FocusEvent) => {
      const el = elementZPodpowiedzia(e.target);
      if (el && el === e.target && el.matches(":focus-visible")) pokaz(el, true);
    };
    const naKlawisz = (e: KeyboardEvent) => {
      if (e.key === "Escape" && cel) ukryj();
    };
    // Klik w „?": pointerdown (faza przechwytywania) już schował kafelek, więc
    // zapamiętujemy, czy był otwarty dla tego przycisku — wtedy klik go zamyka.
    let otwartyPrzed: Element | null = null;
    const przedWcisnieciem = (e: PointerEvent) => {
      const el = e.target instanceof Element ? e.target.closest(POMOC) : null;
      otwartyPrzed = el && el === pokazany ? el : null;
      ukryj();
    };
    const naKlik = (e: MouseEvent) => {
      const el = e.target instanceof Element ? e.target.closest(POMOC) : null;
      if (!el) return;
      e.preventDefault(); // „?" w etykiecie nie przełącza kontrolki
      if (otwartyPrzed === el) otwartyPrzed = null;
      else pokaz(el, true);
    };

    document.addEventListener("pointerover", naNajechanie);
    document.addEventListener("pointerdown", przedWcisnieciem, true);
    document.addEventListener("click", naKlik, true);
    document.addEventListener("focusin", naFokus);
    document.addEventListener("focusout", ukryj);
    document.addEventListener("keydown", naKlawisz, true);
    document.addEventListener("scroll", ukryj, true);
    window.addEventListener("blur", ukryj);
    return () => {
      clearTimeout(zegar);
      document.removeEventListener("pointerover", naNajechanie);
      document.removeEventListener("pointerdown", przedWcisnieciem, true);
      document.removeEventListener("click", naKlik, true);
      document.removeEventListener("focusin", naFokus);
      document.removeEventListener("focusout", ukryj);
      document.removeEventListener("keydown", naKlawisz, true);
      document.removeEventListener("scroll", ukryj, true);
      window.removeEventListener("blur", ukryj);
    };
  }, []);

  // Pozycja po zmierzeniu kafelka: pod elementem, nad nim gdy brak miejsca;
  // w poziomie wyśrodkowany i dociśnięty do krawędzi okna.
  useLayoutEffect(() => {
    if (!stan || !kafelek.current) return;
    const { width, height } = kafelek.current.getBoundingClientRect();
    const r = stan.cel;
    const left = Math.min(
      Math.max(MARGINES, r.left + r.width / 2 - width / 2),
      window.innerWidth - width - MARGINES,
    );
    const pod = r.bottom + MARGINES;
    const top = pod + height <= window.innerHeight - MARGINES ? pod : Math.max(MARGINES, r.top - height - MARGINES);
    setPozycja({ left, top });
  }, [stan]);

  if (!stan) return null;
  return createPortal(
    <div
      ref={kafelek}
      className="podpowiedz"
      role="tooltip"
      style={pozycja ? { left: pozycja.left, top: pozycja.top } : { left: 0, top: 0, visibility: "hidden" }}
    >
      {stan.tekst}
    </div>,
    document.body,
  );
}
